import { useState } from "react";
import { DASH } from "@/constants/testIds";
import { deleteBill, updateBill } from "@/lib/api";
import { toast } from "sonner";

function EditModal({ bill, onClose, onSaved }) {
  const [peak, setPeak] = useState(bill.peak_demand_kw ?? 0);
  const [kvarh, setKvarh] = useState(bill.kvarh_consumed ?? 0);
  const [pf, setPf] = useState(bill.power_factor ?? 0);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateBill(bill.bill_id, {
        peak_demand_kw: Number(peak) || 0,
        kvarh_consumed: Number(kvarh) || 0,
        power_factor: Number(pf) || 0,
      });
      toast.success("Bill updated");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`edit-bill-modal-${bill.bill_id}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1B1A]/60 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#F7F7F4] border border-[#E5E5DE] p-6 sm:p-8 max-w-md w-full"
      >
        <div className="overline mb-2">Edit metrics</div>
        <h3 className="font-display text-xl tracking-tight mb-1">{bill.month_label}</h3>
        <p className="text-xs text-[#6E716C] mb-6 font-mono-data">
          Fill in commercial-tariff values that weren&apos;t on the bill or were missed at upload.
        </p>
        <div className="space-y-4">
          <div>
            <label className="overline mb-1 block">Peak demand (kW)</label>
            <input
              type="number"
              step="0.01"
              value={peak}
              data-testid={`edit-peak-${bill.bill_id}`}
              onChange={(e) => setPeak(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-[#E5E5DE] focus:outline-none focus:border-[#0F3524] text-sm font-body"
            />
          </div>
          <div>
            <label className="overline mb-1 block">Reactive energy (kVARh)</label>
            <input
              type="number"
              step="0.01"
              value={kvarh}
              data-testid={`edit-kvarh-${bill.bill_id}`}
              onChange={(e) => setKvarh(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-[#E5E5DE] focus:outline-none focus:border-[#0F3524] text-sm font-body"
            />
            <p className="text-[10px] text-[#6E716C] mt-1 font-mono-data">
              Meter panel on the last page of your TNB bill (labelled KVARh).
            </p>
          </div>
          <div>
            <label className="overline mb-1 block">Power factor (0–1, or 0 to derive)</label>
            <input
              type="number"
              step="0.01"
              value={pf}
              data-testid={`edit-pf-${bill.bill_id}`}
              onChange={(e) => setPf(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-[#E5E5DE] focus:outline-none focus:border-[#0F3524] text-sm font-body"
            />
            <p className="text-[10px] text-[#6E716C] mt-1 font-mono-data">
              Leave 0 to auto-compute from kWh + kVARh.
            </p>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            data-testid={`edit-save-${bill.bill_id}`}
            disabled={busy}
            className="px-5 py-2.5 bg-[#0F3524] text-[#F7F7F4] hover:bg-[#174D35] disabled:opacity-60 text-sm font-medium tracking-tight"
          >
            {busy ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 border border-[#1A1B1A] text-[#1A1B1A] hover:bg-[#F0F0EA] text-sm font-medium tracking-tight"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default function BillsTable({ months, onChange }) {
  const [editing, setEditing] = useState(null);
  const missingCount = months.filter(
    (m) => (!m.peak_demand_kw || m.peak_demand_kw === 0) || (!m.power_factor || m.power_factor === 0)
  ).length;

  const handleDelete = async (id) => {
    try {
      await deleteBill(id);
      toast.success("Bill removed");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="flat-card overflow-hidden" data-testid={DASH.billsTable}>
      <div className="p-6 sm:p-8 border-b border-[#E5E5DE]">
        <div className="overline mb-2 flex items-center gap-2">
          <span>Ledger</span>
          {missingCount > 0 && (
            <span
              data-testid="ledger-missing-badge"
              title={`${missingCount} bill${missingCount === 1 ? "" : "s"} missing peak demand or power factor`}
              className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] text-[#B24F35] normal-case"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#B24F35] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#B24F35]" />
              </span>
              <span className="uppercase">{missingCount} needs attention</span>
            </span>
          )}
        </div>
        <h3 className="font-display text-xl tracking-tight">Bill-by-bill breakdown</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#F7F7F4]">
              {["Month", "Period", "Days", "kWh", "Peak kW", "PF", "Cost (RM)", "RM/kWh", "Load factor", ""].map((h) => (
                <th
                  key={h}
                  className="text-left font-mono-data uppercase tracking-widest text-[10px] text-[#6E716C] px-4 sm:px-6 py-3 whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {months.map((m) => {
              const missing = (!m.peak_demand_kw || m.peak_demand_kw === 0) || (!m.power_factor || m.power_factor === 0);
              return (
                <tr
                  key={m.bill_id}
                  data-testid={DASH.billRow(m.bill_id)}
                  className="border-b border-[#E5E5DE] hover:bg-[#FAFAF7] transition-colors"
                >
                  <td className="px-4 sm:px-6 py-4 font-display text-base tracking-tight">{m.month_label}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-xs text-[#6E716C] whitespace-nowrap">
                    {m.period_start} → {m.period_end}
                  </td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.days_in_period}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.kwh.toFixed(0)}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">
                    {(m.peak_demand_kw || 0) > 0 ? m.peak_demand_kw.toFixed(2) : <span className="text-[#B24F35]">—</span>}
                  </td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">
                    {(m.power_factor || 0) > 0 ? m.power_factor.toFixed(3) : <span className="text-[#B24F35]">—</span>}
                  </td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.cost_rm.toFixed(2)}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.cost_per_kwh.toFixed(4)}</td>
                  <td className="px-4 sm:px-6 py-4 font-mono-data text-sm">{m.load_factor.toFixed(3)}</td>
                  <td className="px-4 sm:px-6 py-4 text-right whitespace-nowrap">
                    <button
                      data-testid={`edit-bill-btn-${m.bill_id}`}
                      onClick={() => setEditing(m)}
                      className={`text-xs mr-3 transition-colors ${missing ? "text-[#B24F35] font-semibold" : "text-[#6E716C] hover:text-[#0F3524]"}`}
                    >
                      {missing ? "fill in" : "edit"}
                    </button>
                    <button
                      data-testid={DASH.deleteBillBtn(m.bill_id)}
                      onClick={() => handleDelete(m.bill_id)}
                      className="text-xs text-[#6E716C] hover:text-[#B24F35] transition-colors"
                    >
                      remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing && (
        <EditModal
          bill={editing}
          onClose={() => setEditing(null)}
          onSaved={() => onChange?.()}
        />
      )}
    </div>
  );
}
